import { sortBy, uniqBy, uniq, get, compact } from 'lodash';
import { GridApi, ColDef } from '@ag-grid-community/core';

import { getCellAlignment } from 'common/DataTypeFormatter';
import { ExportData, TableColumnFormat } from 'common/visualizations/views/agGridReact/types';
import { OrderConfig } from 'common/visualizations/vif';
import { GROUP_COLUMN_PREFIX, PIVOT_SEPARATOR } from 'common/visualizations/views/agGridReact/Constants';

import { ExtraOptions, GridDataProcessor, GridDataProcessorResult, TableColumn } from '../types';
import { NormalRow } from './type';
import { parseRows } from './gridDataParser';
import { groupRows } from './gridDataGrouper';
import { formatRows } from './gridDataFormatter';
import { buildTotalsRow } from './gridDataAggregator';
import { fetchProcessDiscoveryData } from 'common/visualizations/views/agGridReact/customHooks/useProcessDiscoveryResults';
import { isPivotInGridApi } from 'common/visualizations/helpers/PivotHelpers';
import { SoQLType } from 'common/types/soql';
import { CustomColDefContext } from 'common/types/agGrid/context';

// Note: Retrieving and grouping/formatting data can also be moved to the worker for efficiency.
// Currently, we retrieve 10K records, format/group them in main thread.
// Serialize and send it to worker thread. It again deserializes it (JSON) which is a waste.
// export class GridDataProcessor implements IGridDataProcessor {
export class AgGridDataProcessor implements GridDataProcessor {
  async processGridData(
    gridApi: GridApi,
    getExportData: (selectedFiltered: boolean) => Promise<ExportData>,
    extraOptions: ExtraOptions,
  ): Promise<GridDataProcessorResult> {
    const exportData = await getExportData(true);
    const processDiscoveryResult = await getProcessDiscoveryResult(
      extraOptions,
      exportData.rows as NormalRow[],
    );

    if (isPivotInGridApi(gridApi)) {
      // If we are pivoting, return with pivoted table data
      const pivotRowData = this.getPivotedRows(gridApi);
      const pivotTableColumns = this.buildPivotTableColumns(gridApi, exportData, extraOptions);
      return {
        rowData: pivotRowData,
        tableColumns: pivotTableColumns,
        processDiscoveryResult,
        tableDef: {
          rowHeight: Number(gridApi.getGridOption('rowHeight')) || 10,
        },
        processedRowCount: exportData.rows.length,
        forgeThemeColorConfig: getForgeThemeColorConfig(),
      };
    }

    const tableColumns = this.buildTableColumns(gridApi, exportData, extraOptions);
    const orderConfigs = uniqBy(
      extraOptions.vifOrderConfig.map((orderConfig: OrderConfig) => ({
        ...orderConfig,
        columnName: orderConfig.columnName.replace(`${GROUP_COLUMN_PREFIX}-`, ''),
      })),
      'columnName',
    );
    const parsedRows = parseRows(exportData.rows as NormalRow[], tableColumns);
    const groupedRows = groupRows(
      parsedRows,
      tableColumns,
      orderConfigs,
      extraOptions.showSubTotal,
      extraOptions.columnSortConfig,
    );
    const rowData = formatRows({
      rowData: extraOptions.showTotal
        ? [...groupedRows, buildTotalsRow(groupedRows, tableColumns)]
        : groupedRows,
      tableColumns,
      viewColumns: exportData.columns,
      extraOptions,
      gridApi,
    });

    return {
      rowData,
      tableColumns,
      processDiscoveryResult,
      tableDef: {
        rowHeight: Number(gridApi.getGridOption('rowHeight')) || 10,
      },
      processedRowCount: exportData.rows.length,
      forgeThemeColorConfig: getForgeThemeColorConfig(),
    };
  }

  private buildTableColumns(
    gridApi: GridApi,
    exportData: ExportData,
    extraOptions: ExtraOptions,
  ): TableColumn[] {
    const groupedFields = gridApi.getRowGroupColumns().map((col) => col.getColId());
    const nonGroupedFields = gridApi
      .getAllDisplayedColumns()
      .map((col) => col.getColId())
      .filter((id) => !id.startsWith(GROUP_COLUMN_PREFIX));
    const visibleColumnIds = [...groupedFields, ...nonGroupedFields];

    return sortBy(exportData.columns, (col) => visibleColumnIds.indexOf(col.fieldName))
      .filter((col) => visibleColumnIds.includes(col.fieldName))
      .map((col) => {
        const gridCol = gridApi.getColumn(col.fieldName);
        const colDef = gridCol?.getColDef();
        const columnFormat: TableColumnFormat | undefined = extraOptions.columnFormats[col.fieldName];
        const textAlign = getCellAlignment(col);
        const align = columnFormat?.format?.align;
        const isAlign = columnFormat?.isFormatValue ?? true;
        return {
          field: col.fieldName,
          displayName: columnFormat?.displayName || col.name || col.fieldName,
          type: col.dataTypeName,
          aggFunc: colDef?.aggFunc?.toString(),
          width: gridCol?.getActualWidth(),
          isGrouped: groupedFields.includes(col.fieldName),
          align: isAlign ? align || textAlign : textAlign,
        };
      });
  }

  /**
   * For a pivoted table we show what is displayed in the grid. We do not automatically expand all
   * groups. So if a user has expanded some groups and not others, only show rows in the ones that
   * are expanded. Skip over any rows inside groups that are not expanced.
   */
  private getPivotedRows(gridApi: GridApi): TableColumn[] {
    const rowData: TableColumn[] = [];
    gridApi.forEachNode((node) => {
      if (node?.parent?.expanded === false) {
        return;
      }
      rowData.push(node.data);
    });
    return rowData;
  }

  private buildPivotTableColumns(
    gridApi: GridApi,
    exportData: ExportData,
    extraOptions: ExtraOptions,
  ): TableColumn[] {
    const allDisplayedColumns = gridApi.getAllDisplayedColumns();
    const nonGroupedColumns = allDisplayedColumns.filter((col) => {
      const colId = col.getColId();
      return !colId.startsWith(GROUP_COLUMN_PREFIX);
    });
    const groupedColumns = gridApi.getRowGroupColumns();
    const allColumns = [...groupedColumns, ...nonGroupedColumns];
    const pivotTableColumns = allColumns.map((col) => {
      const colDef = col.getColDef();
      const columnFormat: TableColumnFormat | undefined = extraOptions.columnFormats[col.getColId()];
      const textAlign = getCellAlignment(col);
      const align = columnFormat?.format?.align;
      const isAlign = columnFormat?.isFormatValue ?? true;
      return {
        field: col.getColId(),
        displayName: colDef.headerName,
        type: this.findColumnDataType(colDef, exportData),
        aggFunc: colDef?.aggFunc?.toString(),
        width: col.getActualWidth(),
        isGrouped: false,
        align: isAlign ? align || textAlign : textAlign,
      };
    });
    return pivotTableColumns;
  }

  private findColumnDataType(colDef: ColDef, exportData: ExportData): SoQLType {
    let columnName = colDef.colId;
    if (columnName?.includes(PIVOT_SEPARATOR)) {
      // If this is a pivot column, use it's associated value column name
      columnName = (colDef.context as CustomColDefContext | undefined)?.pivotValueColumn;
    }
    const columnData = exportData.columns.find((col) => col.fieldName === columnName);
    return columnData?.dataTypeName ?? SoQLType.SoQLTextT;
  }
}

const getProcessDiscoveryResult = async (extraOptions: ExtraOptions, rows: NormalRow[]) => {
  const primaryKey = extraOptions.processDiscoveryContext.primaryKeyColumn;
  const selectedColumn = extraOptions.linkedAction?.selectedExistingColumn;

  if (!primaryKey || !selectedColumn) {
    console.log('No primary key or linked action provided for process discovery');
    return {
      applicationDescriptors: [],
      primaryKeyValue: '',
      primaryKeyColumn: primaryKey,
      showHyperLink: false,
      linkedAction: extraOptions.linkedAction,
    };
  }

  const primaryKeyValues = compact(uniq(rows.map((row) => row[primaryKey])));
  const discoveryResponse = await fetchProcessDiscoveryData({
    primaryColumnValue: primaryKeyValues[0],
    contextParams: extraOptions.processDiscoveryContext,
  });

  return {
    applicationDescriptors: get(discoveryResponse, '0.applicationDescriptors', []),
    showHyperLink: true,
    primaryKeyColumn: primaryKey,
    primaryKeyValue: primaryKeyValues[0],
    linkedAction: extraOptions.linkedAction,
  };
};

const getForgeThemeColorConfig = () => {
  return {
    mdcThemeBackground: getCSSVariableValue('--forge-theme-surface-dim'),
    mdcThemeOnSecondary: getCSSVariableValue('--forge-theme-primary'),
    forgeThemeBorderColor: getCSSVariableValue('--forge-theme-border-color'),
    mdcThemeOnPrimary: getCSSVariableValue('--forge-theme-on-primary'),
    pdfPreviewHeaderBackground: getCSSVariableValue('--pdf-preview-header-background'),
  };
};

const getCSSVariableValue = (variableName: string): string => {
  const style = window.getComputedStyle(document.body);
  return style.getPropertyValue(variableName);
};
